import { useEffect, useMemo, useRef, useState } from 'react';
import { lastTekMediaSync, type TekMediaState, type TekMediaSyncStatus } from '../embed/embedBridge';

// Background HAIST↔Tek media sync for one conversation, pushed by the extension
// panel over the embed bridge ('haist-embed:media-sync'). The panel copies media
// off the critical path, so the inspection renders immediately and photo state
// fills in as it lands. Returns the latest per-photo upload state (keyed by the
// photo's storage.gethaist.com URL) + counts. `onLanded` fires when HAIST data
// changed server-side — Tek photos copied in ('in'), or an upload finished ('out'
// entry → done, pulled flag flips) — so the caller can refetch now instead of on
// its next poll. No-op outside the extension (no events arrive).
export function useTekMediaSync(conversationId: number | null | undefined, onLanded?: () => void) {
  // Latest 'out' snapshot for this conversation — each emit carries the RO's full entry set.
  const [out, setOut] = useState<TekMediaSyncStatus | null>(null);
  const onLandedRef = useRef(onLanded);
  useEffect(() => { onLandedRef.current = onLanded; });

  useEffect(() => {
    // Start from the last status seen for this conversation (a long upload emits
    // nothing new until it finishes), not from blank.
    const seed = conversationId ? lastTekMediaSync(conversationId) : null;
    setOut(seed);
    if (!conversationId) return;
    let prev = new Map<string, TekMediaState>((seed?.entries ?? []).map((x) => [x.url, x.state]));
    let prevSig = (seed?.entries ?? []).map((x) => `${x.url}:${x.state}`).join('|');
    const onSync = (e: Event) => {
      const s = (e as CustomEvent<TekMediaSyncStatus>).detail;
      if (!s || s.convId !== conversationId) return;
      if (s.direction === 'in') { onLandedRef.current?.(); return; }
      const landed = s.entries.some((x) => x.state === 'done' && prev.get(x.url) !== 'done');
      prev = new Map(s.entries.map((x) => [x.url, x.state]));
      // Skip identical re-emits (e.g. a deferred retry re-marking failed) — no re-render.
      const sig = s.entries.map((x) => `${x.url}:${x.state}`).join('|');
      if (sig !== prevSig) { prevSig = sig; setOut(s); }
      if (landed) onLandedRef.current?.();
    };
    window.addEventListener('haist-embed:media-sync', onSync);
    return () => window.removeEventListener('haist-embed:media-sync', onSync);
  }, [conversationId]);

  const byUrl = useMemo(
    () => new Map<string, TekMediaState>((out?.entries ?? []).map((x) => [x.url, x.state])),
    [out],
  );
  const summary = useMemo(
    () => ({ pending: out?.pending ?? 0, uploading: out?.uploading ?? 0, failed: out?.failed ?? 0 }),
    [out],
  );
  return { byUrl, summary };
}
